import type {
  Primitive,
  CamelCase,
  SnakeCase,
  SnakeCasedPropertiesDeep,
} from 'type-fest';

// NB imported by path, not via the `@frontend/api` barrel, so this module only
// pulls in the geometric analysis wire types.
import {
  GEOMETRIC_ANALYSIS_FEEDBACK_LEVEL,
  type GeometricAnalysisDbDatum,
  type GeometricAnalysisFeature,
  type GeometricAnalysisFeedbackItem,
  type GeometricAnalysisFeedbackLevel,
  type GeometricAnalysisVector3,
} from '@frontend/api/src/parts/geometricAnalysis/types';

import {
  COLOR_CODES,
  DFM_DESCRIPTIONS,
  ENTITY_META_DATA_INFO,
  EXPLICIT_ADDITIONAL_DISPLAY_TYPES,
  FAMILY_MAP,
  FEATURE_PROP_TYPES,
  KG_PER_LB,
  MM_PER_INCH,
  QUICK_DESCRIPTION,
  SPLIT_CHAR,
  UNITS,
  VOLUME_DISPLAY_KEYS,
  WEIGHT_UNITS,
} from './constants';
import type { ADDITIONAL_DISPLAY_TYPES } from './constants';
import type { PointJson } from './contracts/basic';
import type { AdditionalViewerContent } from './renderer/creation-utils';
import type { MouseMarker } from './renderer/renderer';

// NOTE: not importing this from geom-utils because of some weird import build compile iisue
// with babel. The THREE.js import is problematic into this file
const isClose = (a: number, b: number, tol = 1e-4): boolean => {
  return Math.abs(a - b) < tol;
};

export const roundFloatingPoint = (
  number: number,
  roundingHash: number,
  roundDown = false,
): number => {
  let neg = false;
  if (number < 0) {
    neg = true;
  }
  const percentage = Math.pow(10, -roundingHash - 1);
  const base = number - (number % Math.pow(10, -roundingHash));
  const remainder = Math.abs(number - base);
  const diff = Math.abs(remainder - 5 * percentage);
  if (remainder === 0) {
    return parseFloat(number.toFixed(roundingHash));
  }
  if (diff / remainder <= percentage || isClose(diff / remainder, percentage)) {
    if ((roundDown && neg) || (!roundDown && !neg)) {
      const temp = number + diff + percentage;
      return parseFloat(temp.toFixed(roundingHash));
    } else {
      const temp = number - diff - percentage;
      return parseFloat(temp.toFixed(roundingHash));
    }
  }
  return parseFloat(number.toFixed(roundingHash));
};

export const time = () => new Date().getTime() / 1000;

export const identity = <T>(x: T): T => x;
type Getter<T, R> = (x: T) => R;

export const areArraysEqual = <T>(
  a: T[],
  b: T[],
  getter: Getter<T, unknown> = identity as Getter<T, T>,
): boolean => {
  let result = a.length === b.length;
  if (result) {
    for (let i = 0; i < a.length; i++) {
      if (getter(a[i]) !== getter(b[i])) {
        result = false;
        break;
      }
    }
  }
  return result;
};

export const areEntitiesStringEqual = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

export const areObjectsDifferentForKeys = <T extends Record<string, unknown>>(
  a: T,
  b: T,
  keys: (keyof T)[],
): boolean => {
  for (const key of keys) {
    if (a[key] !== b[key]) {
      return true;
    }
  }
  return false;
};

//areArraysAsSetDifferent: For comparing arrays like Sets
export const areArraysAsSetDifferent = <T>(a: T[], b: T[]): boolean => {
  if (a === b) {
    return false;
  }
  if (a.length === 0 && b.length === 0) {
    return false;
  }
  const aSet = new Set(a);
  const bSet = new Set(b);
  if (aSet.size !== bSet.size) {
    return true;
  }
  //sizes are same so only need to check items from one set
  for (const item of aSet) {
    if (!bSet.has(item)) {
      return true;
    }
  }
  return false;
};

export const areSetsDifferent = <T>(a: Set<T>, b: Set<T>) => {
  if (a === b) {
    return false;
  }
  if (a.size === 0 && b.size === 0) {
    return false;
  }
  if (a.size !== b.size) {
    return true;
  }
  //sizes are same so only need to check items from one set
  for (const item of a) {
    if (!b.has(item)) {
      return true;
    }
  }
  return false;
};

export const objectWithoutProperties = <T extends Record<string, unknown>>(
  obj: T,
  keys: (keyof T)[],
): Omit<T, (typeof keys)[number]> => {
  const target = {} as Omit<T, (typeof keys)[number]>;
  for (const i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i as keyof Omit<T, keyof T>] = obj[i as keyof T] as T[Exclude<
      keyof T,
      keyof T
    >];
  }
  return target;
};

export const getNodeComputedProperty = (
  node: HTMLElement,
  prop: string,
): string => window.getComputedStyle(node, null).getPropertyValue(prop);

const clamp0 = (v: number) => (v < 0 ? 0 : v);

export const getRelativeCoordinates = (
  e: WheelEvent | MouseEvent,
  container: HTMLElement,
): MouseMarker => {
  //Note: Adjusting the offsetLeft, offsetTop is important for zooming on the mouse's position
  let { offsetLeft, offsetTop, offsetParent } = container;
  while (offsetParent instanceof HTMLElement) {
    //Important: Must consider offsetParent.scrollLeft and offsetParent.scrollTop
    //for digiquote because of the scrollbar
    offsetLeft += offsetParent.offsetLeft - offsetParent.scrollLeft;
    offsetTop += offsetParent.offsetTop - offsetParent.scrollTop;
    offsetParent = offsetParent.offsetParent;
  }
  const x = e.pageX - offsetLeft;
  const y = e.pageY - offsetTop;
  //clamp x and y to 0. This is necessary for digiquote. We get mouse events outside the canvas for a few pixels
  return { x: clamp0(x), y: clamp0(y) };
};

export const getRelativeTouchCoordinates = (
  { touches: [touchStart, touchEnd] }: TouchEvent,
  container: HTMLElement,
): [MouseMarker, MouseMarker] => {
  let { offsetLeft, offsetTop, offsetParent } = container;
  while (offsetParent instanceof HTMLElement) {
    //Important: Must consider offsetParent.scrollLeft and offsetParent.scrollTop
    //for digiquote because of the scrollbar
    offsetLeft += offsetParent.offsetLeft - offsetParent.scrollLeft;
    offsetTop += offsetParent.offsetTop - offsetParent.scrollTop;
    offsetParent = offsetParent.offsetParent;
  }
  //clamp x and y to 0. This is necessary for digiquote. We get mouse events outside the canvas for a few pixels
  return [
    {
      x: clamp0(touchStart.pageX - offsetLeft),
      y: clamp0(touchStart.pageY - offsetTop),
    },
    {
      x: clamp0(touchEnd.pageX - offsetLeft),
      y: clamp0(touchEnd.pageY - offsetTop),
    },
  ];
};

const capitalize = (str: string): string =>
  str && str.charAt(0).toUpperCase() + str.slice(1);

const toCamelCase = (str: string): CamelCase<typeof str> => {
  if (str.includes(' ')) return str as CamelCase<typeof str>;
  return str.replace(/^([A-Z])|[\s-_](\w)/g, (_, p1, p2) => {
    if (p2) return p2.toUpperCase();
    return p1.toLowerCase();
  }) as CamelCase<typeof str>;
};

export const keysToCamelCase = <T extends Record<string, unknown>>(
  obj: T,
): { [camelCaseKey: string]: unknown } => {
  return Object.keys(obj).reduce((acc, key) => {
    acc[toCamelCase(key)] = obj[key];
    return acc;
  }, {} as { [camelCaseKey: string]: unknown });
};

const toSnakeCase = (str: string): SnakeCase<typeof str> =>
  str.replace(/^([A-Z])|([A-Z])|[\s-](\w)/g, (_, p1, p2, p3) => {
    if (p2) return '_' + p2.toLowerCase();
    if (p3) return '_' + p3.toLowerCase();
    return p1.toLowerCase();
  });

type SnakeCaseablePrimitive = Primitive;
type SnakeCaseableArray = SnakeCaseable[];
type SnakeCaseableObject = { [Key in string]?: SnakeCaseable };
type SnakeCaseable =
  | SnakeCaseableObject
  | SnakeCaseableArray
  | SnakeCaseablePrimitive;

//TODO: This implementation doesn't seem as strong as it could be
export const keysToSnakeCaseDeep = <T extends SnakeCaseable>(
  obj: T,
): SnakeCasedPropertiesDeep<T> => {
  if (Array.isArray(obj)) {
    return obj.map((element) =>
      keysToSnakeCaseDeep(element),
    ) as SnakeCasedPropertiesDeep<T>;
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      if (key === 'overrides') {
        //eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        acc[key] = obj[key];
      } else {
        if (obj[key] instanceof Object) {
          //eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          acc[key.includes(' ') ? key : toSnakeCase(key)] = keysToSnakeCaseDeep(
            obj[key],
          );
        } else {
          //eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          acc[key.includes(' ') ? key : toSnakeCase(key)] = obj[key];
        }
      }
      return acc;
    }, {} as SnakeCasedPropertiesDeep<T>);
  } else {
    return obj as SnakeCasedPropertiesDeep<T>; //primitive
  }
};

const toReadableString = (str: string): string =>
  toSnakeCase(str).replace(/_/g, ' ');

export const toCapitalizedReadableString = (str: string): string => {
  const lower = toReadableString(str);
  const capitalizedArray = lower.split(' ').map((s) => capitalize(s));
  return capitalizedArray.join(' ');
};

export const makeDimensionalStringWithUnits = ({
  value,
  order,
  displayUnits,
  dimensionalPrecision,
  dropZeroes = true,
}: {
  value: number;
  order?: number | null;
  displayUnits: UNITS;
  dimensionalPrecision: number;
  dropZeroes: boolean;
}): string => {
  const unitsPrefix = order === 3 ? 'cu. ' : order === 2 ? 'sq. ' : '';
  return dropZeroes
    ? `${+value.toFixed(dimensionalPrecision)} ${unitsPrefix}${displayUnits}`
    : `${value.toFixed(dimensionalPrecision)} ${unitsPrefix}${displayUnits}`;
};

export const convert = (
  value: number,
  displayUnits: UNITS,
  nativeUnits: UNITS,
  order: number,
): number => {
  const nativeIn = nativeUnits === UNITS.IN;
  const displayNative = displayUnits === UNITS.NATIVE;
  const displayIn = displayUnits === UNITS.IN;
  return displayNative
    ? value
    : displayIn
    ? nativeIn
      ? value
      : value / Math.pow(MM_PER_INCH, order)
    : nativeIn
    ? value * Math.pow(MM_PER_INCH, order)
    : value;
};

export const formatLengthProp = ({
  value,
  order,
  displayUnits,
  nativeUnits,
  dimensionalPrecision,
  displayScale,
  dropZeroes = false,
}: {
  value: number;
  order?: number | null;
  displayUnits: UNITS;
  nativeUnits: UNITS;
  dimensionalPrecision: number;
  displayScale: number;
  dropZeroes?: boolean;
}): string => {
  return makeDimensionalStringWithUnits({
    value:
      convert(value, displayUnits, nativeUnits, order as number) *
      Math.pow(displayScale, order as number),
    order,
    displayUnits,
    dimensionalPrecision,
    dropZeroes,
  });
};

export const formatWeightProp = ({
  weight,
  displayWeightUnits,
  dimensionalPrecision,
  displayScale,
}: {
  weight: number;
  displayWeightUnits: WEIGHT_UNITS;
  dimensionalPrecision: number;
  displayScale: number;
}): string =>
  weight == null
    ? '---'
    : displayWeightUnits === WEIGHT_UNITS.KG
    ? `${+(weight * KG_PER_LB * Math.pow(displayScale, 3)).toFixed(
        dimensionalPrecision,
      )} kg`
    : `${+(weight * Math.pow(displayScale, 3)).toFixed(
        dimensionalPrecision,
      )} lb`;

const formatAngleProp = ({
  value,
  convertAngle,
  dimensionalPrecision,
}: {
  value: number;
  convertAngle?: boolean;
  dimensionalPrecision: number;
}): string => {
  if (convertAngle) {
    return `${+((180 / Math.PI) * value).toFixed(dimensionalPrecision)} deg`;
  } else {
    return `${+value.toFixed(dimensionalPrecision)} deg`;
  }
};

const formatPointVector = ({
  value,
  displayUnits,
  nativeUnits,
  dimensionalPrecision,
  displayScale,
}: {
  value: PointJson;
  displayUnits: UNITS;
  nativeUnits: UNITS;
  dimensionalPrecision: number;
  displayScale: number;
}): string => {
  return `(${+convert(
    value[0] * displayScale,
    displayUnits,
    nativeUnits,
    1,
  ).toFixed(dimensionalPrecision)}, ${+convert(
    value[1] * displayScale,
    displayUnits,
    nativeUnits,
    1,
  ).toFixed(dimensionalPrecision)}, ${+convert(
    value[2] * displayScale,
    displayUnits,
    nativeUnits,
    1,
  ).toFixed(dimensionalPrecision)})`;
};

const formatUnitVector = ({
  value,
  dimensionalPrecision,
}: {
  value: PointJson;
  dimensionalPrecision: number;
}): string => {
  return `(${+value[0].toFixed(dimensionalPrecision)}, ${+value[1].toFixed(
    dimensionalPrecision,
  )}, ${+value[2].toFixed(dimensionalPrecision)})`;
};

/** Display settings the formatters apply to a stored value. */
type FormatDisplayContext = {
  convertAngle?: boolean;
  dimensionalPrecision: number;
  displayScale: number;
  displayUnits: UNITS;
  nativeUnits: UNITS;
};

/** The part of a `dbData` row (or entity metadata entry) a formatter reads. */
type FormattableValue = Pick<GeometricAnalysisDbDatum, 'type' | 'value'> & {
  order?: number | null;
};

export const formatEntityValueForDisplay = ({
  value,
  type,
  order,
  displayUnits,
  nativeUnits,
  dimensionalPrecision,
  displayScale,
  convertAngle = true,
}: FormattableValue & FormatDisplayContext): string => {
  switch (type) {
    case FEATURE_PROP_TYPES.LENGTH:
      return formatLengthProp({
        value: value as number,
        order,
        displayUnits,
        nativeUnits,
        displayScale,
        dimensionalPrecision,
      });
    case FEATURE_PROP_TYPES.ANGLE:
      return formatAngleProp({
        value: value as number,
        convertAngle,
        dimensionalPrecision,
      });
    case FEATURE_PROP_TYPES.NON_DIMENSIONAL_NUMERIC:
      return `${+(value as number).toFixed(dimensionalPrecision)}`;
    case FEATURE_PROP_TYPES.BOOL:
      return value.toString();
    case FEATURE_PROP_TYPES.POINT_VECTOR:
      return formatPointVector({
        value: value as PointJson,
        displayUnits,
        nativeUnits,
        displayScale,
        dimensionalPrecision,
      });
    case FEATURE_PROP_TYPES.UNIT_VECTOR:
      return formatUnitVector({
        value: value as PointJson,
        dimensionalPrecision,
      });
    case FEATURE_PROP_TYPES.STRING:
    case FEATURE_PROP_TYPES.ABSTRACT:
      return value.toString();
    default:
      return '';
  }
};

const compareByName = (
  a: GeometricAnalysisDbDatum,
  b: GeometricAnalysisDbDatum,
) => a.name.toLowerCase().localeCompare(b.name.toLowerCase());

export const constructFeatureDescriptorString = (
  feature: { dbData: readonly GeometricAnalysisDbDatum[] },
  displayUnits: UNITS,
  nativeUnits: UNITS,
  dimensionalPrecision: number,
  displayScale = 1.0,
): string => {
  const out = feature.dbData.slice();
  return out
    .sort(compareByName)
    .map((entry) => {
      return `${toCapitalizedReadableString(
        entry.name,
      )}: ${formatEntityValueForDisplay({
        ...entry,
        displayUnits,
        nativeUnits,
        dimensionalPrecision,
        displayScale,
      })}`;
    })
    .join(', ');
};

export const constructFeatureComparatorString = (
  feature: { dbData: readonly GeometricAnalysisDbDatum[] },
  displayUnits: UNITS,
  nativeUnits: UNITS,
  dimensionalPrecision: number,
  displayScale = 1.0,
): string | null => {
  let comparatorString = '';
  const arr = feature.dbData.slice().sort(compareByName);
  for (const entry of arr) {
    // construct comparator string using the precision we will use for the descriptor string
    comparatorString += formatEntityValueForDisplay({
      ...entry,
      displayUnits,
      nativeUnits,
      dimensionalPrecision,
      displayScale,
    });
  }
  return comparatorString || null;
};

export type MetaData = {
  type?: FEATURE_PROP_TYPES;
  label?: string;
} & Record<string, number | boolean | string | PointJson>;

export const formatEntityMetaData = (
  metaData: MetaData,
  displayUnits: UNITS,
  nativeUnits: UNITS,
  dimensionalPrecision: number,
  displayScale = 1.0,
) => {
  const out = [];
  const refData: MetaData = metaData || {};
  for (const entry of ENTITY_META_DATA_INFO) {
    const { key, type, label, order } = entry;
    if (refData[key] && refData[key] != null) {
      // NOTE: for entity meta data, the angle values are stored in the JSON in degrees, not radians like db data
      const value = formatEntityValueForDisplay({
        value: refData[key] as number | boolean | PointJson,
        type,
        order: order as number,
        displayUnits,
        dimensionalPrecision,
        nativeUnits,
        displayScale,
        convertAngle: false,
      });
      out.push({ title: label, payload: value });
    }
  }
  return out;
};

/**
 * Constructs the feature key name with body, entity type and entity index encoded in the name so that these parameters can be decoded after doing intersection tests of these entites
 * @param referenceUuid
 * @param indicator
 * @param featureId
 */
export const getFeatureKey = (
  ...args: [string, string, string | number]
): string => args.join(SPLIT_CHAR);

// Properties truly common between features and feedback. Code that handles
// either kind (the feature tree, snap-to) should accept this, not the union.
export type PreparedViewerItem = {
  // Core identification
  authenticatedUrl: string | null;
  key: string;
  id: number;
  isInnate?: boolean;
  referenceUuid: string;
  name: string;
  message: string | undefined;

  faces: number[];
  edges: number[];

  dbData: GeometricAnalysisDbDatum[];

  /**
   * Numeric hex from COLOR_CODES (e.g. 0xffdc00). Undefined when neither the
   * label nor the family name is in that table. 0 is a real color.
   */
  color: number | undefined;
  family: string | undefined;

  // Camera/viewport properties
  canSnap: boolean;
  cameraPosition?: GeometricAnalysisVector3 | undefined;
  controlTarget?: GeometricAnalysisVector3 | undefined;
  canTag: boolean;

  // The same item on the part file's flat counterpart, when it has one
  flatFeature?: PreparedFeature | PreparedFeedbackItem | null;

  // Additional content
  additionalViewerContentInfo: unknown;
  additionalViewerContent: AdditionalViewerContent;
  needsLoading: boolean;

  // Metadata
  isLegacy: boolean;
  isAdditionalContentExplicit: boolean;
};

export type PreparedFeature = PreparedViewerItem & {
  // Feature-specific identification
  flatFeatureId?: number | null;

  // Feature volume properties
  canShowFeatureVolume: boolean;
  featureVolume: number;
};

export type PreparedFeedbackItem = PreparedViewerItem & {
  // Feedback-specific properties
  exclusion: boolean;
  level: GeometricAnalysisFeedbackLevel;

  // Feedback-specific identification
  flatFeedbackItemId?: number | null;

  // Transformation data — wire shape; often null for CAD feedback
  transform: number[] | null;
};

/** COLOR_CODES values that are numeric hex, including black (0). */
const numericColorCode = (key: string | undefined): number | undefined => {
  if (key == null) return undefined;
  const color = COLOR_CODES[key as keyof typeof COLOR_CODES];
  return typeof color === 'number' ? color : undefined;
};

/*
  Take a rawFeature directly from serializer form and appends extra information so it
  can be manipulated and properly referenced in the viewer scene.
  The indicator is something used to make sure that the generate key is unique to the
  part it belongs do. For legacy features, this will be a document hash associated with
  a data packet. For new features, it will simply be a short string to differentiate between
  feature DB ids and feedback item DB ids
*/
export const prepareRawFeature = (
  rawFeature: GeometricAnalysisFeature,
  indicator: string,
  needsLoading: boolean,
  viewerContent: AdditionalViewerContent,
): PreparedFeature => {
  const {
    additionalViewerContentInfo: rawAdditionalViewerContentInfo,
    dbData,
    id: rawFeatureId, // either a DB reference id, an internal ID from pint for PCAD, or a dynamically constructed id from a legacy feature
    referenceUuid, // uuid of part this feature belongs to in 3D scene
    viewpoint,
  } = rawFeature;
  // keys to snake case for key matching comparisons in dbData and url fetched viewerContent field
  const additionalViewerContentInfo = keysToSnakeCaseDeep(
    rawAdditionalViewerContentInfo,
  );
  const labelValue = dbData.find((row) => row.name === 'label')?.value;
  const label = typeof labelValue === 'string' ? labelValue : undefined;
  const familyName = FAMILY_MAP[rawFeature.name as keyof typeof FAMILY_MAP];
  const volume = dbData.find((e) => e.name === 'volume')?.value;

  const canShowFeatureVolume =
    Object.keys(additionalViewerContentInfo || {}).filter((k) =>
      VOLUME_DISPLAY_KEYS.includes(k),
    ).length > 0;
  return {
    authenticatedUrl: rawFeature.authenticatedUrl,
    key: getFeatureKey(referenceUuid, indicator, rawFeatureId),
    id: rawFeatureId,
    isInnate: rawFeature.isInnate,
    referenceUuid,
    name: rawFeature.name,
    message: DFM_DESCRIPTIONS[rawFeature.name as keyof typeof DFM_DESCRIPTIONS],
    faces: rawFeature.faces,
    edges: rawFeature.edges,
    color: numericColorCode(label) ?? numericColorCode(familyName),
    family: familyName,
    canSnap: !!viewpoint,
    cameraPosition: viewpoint?.position,
    controlTarget: viewpoint?.target,
    canTag: true,
    dbData,
    flatFeatureId: rawFeature.flatFeatureId,
    flatFeature: null,
    additionalViewerContentInfo,
    additionalViewerContent: viewerContent, // NOTE: this will be loaded in on demand
    needsLoading,
    isLegacy: false, // for use in creating additional feedback
    // special properties for showing feature volume display in setups UI
    isAdditionalContentExplicit:
      Object.keys(additionalViewerContentInfo || {}).filter((k) =>
        EXPLICIT_ADDITIONAL_DISPLAY_TYPES.includes(
          k as ADDITIONAL_DISPLAY_TYPES,
        ),
      ).length > 0,
    canShowFeatureVolume,
    featureVolume:
      canShowFeatureVolume && typeof volume === 'number' ? volume : 0,
  };
};

export const prepareRawFeedback = (
  rawFeedback: GeometricAnalysisFeedbackItem,
  indicator: string,
  needsLoading: boolean,
  viewerContent: AdditionalViewerContent,
): PreparedFeedbackItem => {
  const {
    additionalViewerContentInfo: rawAdditionalViewerContentInfo,
    dbData,
    id: rawFeatureId, // either a DB reference id, an internal ID from pint for PCAD, or a dynamically constructed id from a legacy feature
    referenceUuid, // uuid of part this feature belongs to in 3D scene
    viewpoint,
  } = rawFeedback;
  // keys to snake case for key matching comparisons in dbData and url fetched viewerContent field
  const additionalViewerContentInfo = keysToSnakeCaseDeep(
    rawAdditionalViewerContentInfo,
  );

  return {
    authenticatedUrl: rawFeedback.authenticatedUrl,
    key: getFeatureKey(referenceUuid, indicator, rawFeatureId),
    id: rawFeatureId,
    isInnate: rawFeedback.isInnate,
    referenceUuid,
    name: rawFeedback.name,
    message:
      DFM_DESCRIPTIONS[rawFeedback.name as keyof typeof DFM_DESCRIPTIONS],
    faces: rawFeedback.faces,
    edges: rawFeedback.edges,
    exclusion:
      rawFeedback.level ===
      GEOMETRIC_ANALYSIS_FEEDBACK_LEVEL.COMPLETE_EXCLUSION,
    level: rawFeedback.level,
    color:
      rawFeedback.level === GEOMETRIC_ANALYSIS_FEEDBACK_LEVEL.COMPLETE_EXCLUSION
        ? COLOR_CODES.EXCLUSION
        : COLOR_CODES.ISSUE,
    family: QUICK_DESCRIPTION[rawFeedback.name],
    canSnap: !!viewpoint,
    cameraPosition: viewpoint?.position,
    controlTarget: viewpoint?.target,
    canTag: true,
    dbData,
    flatFeedbackItemId: rawFeedback.flatFeedbackItemId,
    flatFeature: null,
    additionalViewerContentInfo,
    additionalViewerContent: viewerContent, // NOTE: this will be loaded in on demand
    needsLoading,
    transform: rawFeedback.transform,
    isLegacy: false, // for use in creating additional feedback
    isAdditionalContentExplicit:
      Object.keys(additionalViewerContentInfo || {}).filter((k) =>
        EXPLICIT_ADDITIONAL_DISPLAY_TYPES.includes(
          k as ADDITIONAL_DISPLAY_TYPES,
        ),
      ).length > 0,
  };
};

type Task = Record<string, unknown>;

type PartData = {
  features: { isInnate?: boolean; referenceUuid: string; key: string }[];
  feedbackItems: { isInnate?: boolean; referenceUuid: string; key: string }[];
  data: unknown;
  //eslint-disable-next-line camelcase
  data_packets: {
    referenceUuid: string;
    version: number;
    task: Task;
    documentHash: string;
    featureKeys: string[];
    feedbackKeys: string[];
  }[];
};

export const getFeatures = (
  partData: PartData,
  task: Task,
  documentHash: string,
  referenceUuid: string,
) => {
  const innate = partData.features.filter(
    (f) => f.referenceUuid === referenceUuid && f.isInnate,
  );
  // if no document hash, grab first applicable task lookup for the uuid
  const relevantPacket = partData.data_packets
    .filter(
      (dp) =>
        dp.referenceUuid === referenceUuid &&
        dp.task === task &&
        (documentHash ? dp.documentHash === documentHash : true),
    )
    .sort((a, b) => b.version - a.version)[0];

  if (relevantPacket) {
    return innate.concat(
      partData.features.filter((f) =>
        relevantPacket.featureKeys.includes(f.key),
      ),
    );
  } else {
    return innate;
  }
};

export const getFeedback = (
  partData: PartData,
  task: Task,
  documentHash: string,
  referenceUuid: string,
) => {
  const innate = partData.feedbackItems.filter(
    (f) => f.referenceUuid === referenceUuid && f.isInnate,
  );
  // if no document hash, grab first applicable task lookup for the uuid
  const relevantPacket = partData.data_packets
    .filter(
      (dp) =>
        dp.referenceUuid === referenceUuid &&
        dp.task === task &&
        (documentHash ? dp.documentHash === documentHash : true),
    )
    .sort((a, b) => b.version - a.version)[0];

  if (relevantPacket) {
    return innate.concat(
      partData.feedbackItems.filter((f) =>
        relevantPacket.feedbackKeys.includes(f.key),
      ),
    );
  } else {
    return innate;
  }
};
